"use client";

import { HeartIcon } from "icons";
import React, { useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { Link, Text } from "ui-components";

import { makeToast } from "@/src/components/Toast/Toast";
import {
  JobResponseEntity,
  SimilarJobItem,
  useJobFavouriteToggle,
} from "@/src/hooks/api/generated";
import { useAuth } from "@/src/hooks/useAuth";

import { useJobFavorite } from "./JobFavoriteContext";

export interface IToggleSaveJobClient {
  jobDetails: SimilarJobItem | JobResponseEntity;
  isInFloating?: boolean;
  isCircle?: boolean;
  useLocalState?: boolean;
}

export function ToggleSaveJobClient({
  jobDetails,
  isInFloating = false,
  isCircle = false,
  useLocalState = false,
}: IToggleSaveJobClient): JSX.Element {
  const { isAuthenticated } = useAuth();

  // Always call all hooks unconditionally (Rules of Hooks)
  const [localFavourite, setLocalFavourite] = useState<boolean | undefined>(
    jobDetails?.is_favourite
  );
  const contextValue = useJobFavorite();

  // Choose which state to use based on the prop
  const isFavourite = useLocalState ? localFavourite : contextValue.isFavourite;
  const setIsFavourite = useLocalState
    ? setLocalFavourite
    : contextValue.setIsFavourite;

  const { mutate: toggleFavourite } = useJobFavouriteToggle();

  function handleFavouriteJobToggle(): void {
    if (!jobDetails?.slug || !setIsFavourite) {
      return;
    }
    toggleFavourite(
      {
        data: {
          jobSlug: jobDetails?.slug,
        },
      },
      {
        onSuccess: () => {
          setIsFavourite(!isFavourite);
          makeToast({
            title: (
              <div>
                <Text className="mr-2">
                  {isFavourite ? "Job removed." : "You have saved a job."}
                </Text>
                <Link href="/profile/saved-jobs">
                  <Text>See all your saved jobs</Text>
                </Link>
              </div>
            ),
            intent: isFavourite ? "danger" : "success",
          });
        },
      }
    );
  }

  return (
    <div className="hidden md:block">
      {isAuthenticated && (
        <AriaButton
          className="outline-none"
          onPress={() => {
            handleFavouriteJobToggle();
          }}
        >
          <div className="flex flex-col gap-1 justify-center items-center">
            {!isInFloating && <Text>Save this job</Text>}

            {isCircle ? (
              <HeartIcon
                className={`w-5 h-5 text-brand-primary ${
                  isFavourite ? "fill-brand-primary" : ""
                }`}
              />
            ) : (
              <div className="w-8 h-8 flex items-center justify-center rounded-full border-brand-primary border-2">
                <HeartIcon
                  className={`w-5 h-5 text-brand-primary ${
                    isFavourite ? "fill-brand-primary" : ""
                  }`}
                />
              </div>
            )}
          </div>
        </AriaButton>
      )}
    </div>
  );
}
